import { queryOptions, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { api, ApiOptions, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createQueryString, createURLPath } from "@/lib/utils";
import { APIResponseSuccessSchema } from "@/schema/api";
import { ConsumerResearchOverviewStatsSchema } from "@/schema/research/consumer";

type QueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export type QueueType = "review_needed" | "flag" | "all";

export interface FetchBrandModerationPagedParams {
  page: number;
  size: number;
  queue_type: QueueType;
}

export interface ActionLogsParams {
  page: number;
  size: number;
}

/** Default page size for moderation queue, flags list, and action logs APIs */
export const BRAND_MODERATION_PAGE_SIZE = 20;

/** GET /api/brand/moderation/flags — item shape */
const BrandModerationFlagListItemSchema = z
  .object({
    id: z.string(),
    brand_name: z.string(),
    platform: z.string(),
    comment_id: z.string(),
    comment_text: z.string(),
    post_id: z.string(),
    post_text: z.string(),
    comment_user_name: z.string(),
    category: z.string(),
    flag_reason: z.string(),
    matched_rule_summary: z.string(),
    applied_rule_neo4j_id: z.string().nullable(),
    applied_rule_text: z.string().nullable(),
    confidence_score: z.number(),
    priority: z.string(),
    status: z.string(),
    actioned_type: z.string().nullable(),
    actioned_by: z.string().nullable(),
    actioned_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

export type BrandModerationFlagListItem = z.infer<typeof BrandModerationFlagListItemSchema>;

/** GET /api/brand/moderation/queue — item shape */
const BrandModerationQueueListItemSchema = z
  .object({
    id: z.string(),
    brand_name: z.string(),
    platform: z.string(),
    comment_id: z.string(),
    comment_text: z.string(),
    post_id: z.string(),
    post_text: z.string(),
    comment_user_name: z.string(),
    category: z.string(),
    ai_draft_response: z.string().nullable(),
    matched_rule_summary: z.string(),
    applied_rule_neo4j_id: z.string().nullable().optional(),
    applied_rule_text: z.string().nullable().optional(),
    confidence_score: z.number(),
    priority: z.string(),
    status: z.string(),
    resolved_action: z.string().nullable().optional(),
    resolved_by: z.string().nullable().optional(),
    resolved_at: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
    playbook_name: z.string().nullable().optional(),
  })
  .passthrough();

export type BrandModerationQueueListItem = z.infer<typeof BrandModerationQueueListItemSchema>;

/** One entry from `matched_rules` on action-log / moderation payloads */
// const BrandModerationMatchedRuleSchema = z
//   .object({
//     actions: z.array(z.string()),
//     perform: z.string(),
//     trigger: z.string(),
//     platform: z.array(z.string()),
//     trigger_params: z.array(z.string()),
//     perform_confidence_score: z.number(),
//   })
//   .passthrough();

// export type BrandModerationMatchedRule = z.infer<typeof BrandModerationMatchedRuleSchema>;

/** GET /api/brand/moderation/action-logs — item shape (optional fields vary by backend; passthrough keeps extras) */
const BrandModerationActionLogItemSchema = z
  .object({
    id: z.string(),
    brand_name: z.string().optional(),
    platform: z.string().optional(),
    comment_id: z.string().optional(),
    comment_text: z.string().optional(),
    post_id: z.string().optional(),
    post_text: z.string().optional(),
    comment_user_name: z.string().optional(),
    category: z.string().optional(),
    matched_rule_summary: z.string().optional(),
    applied_rule_text: z.string().optional().nullable(),
    ai_draft_response: z.string().optional(),
    confidence_score: z.number().optional(),
    priority: z.string().optional(),
    status: z.string().optional(),
    actioned_type: z.string().nullable().optional(),
    actioned_by: z.string().nullable().optional(),
    actioned_at: z.string().nullable().optional(),
    resolved_action: z.string().nullable().optional(),
    resolved_by: z.string().nullable().optional(),
    resolved_at: z.string().nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    action_taken: z.string(),
    playbook_name: z.string().nullable().optional(),
    reasoning: z.string().nullable(),
    // matched_rules: z.array(BrandModerationMatchedRuleSchema).optional(),
  })
  .passthrough();

export type BrandModerationActionLogItem = z.infer<typeof BrandModerationActionLogItemSchema>;

const BrandModerationFlagPaginatedListDataSchema = z
  .object({
    items: z.array(BrandModerationFlagListItemSchema),
    total: z.number(),
    page: z.number(),
    size: z.number(),
    pages: z.number(),
  })
  .passthrough();

const BrandModerationQueuePaginatedListDataSchema = z
  .object({
    items: z.array(BrandModerationQueueListItemSchema),
    total: z.number(),
    page: z.number(),
    size: z.number(),
    pages: z.number(),
  })
  .passthrough();

export type BrandModerationFlagPaginatedListPage = z.infer<typeof BrandModerationFlagPaginatedListDataSchema>;
export type BrandModerationQueuePaginatedListPage = z.infer<typeof BrandModerationQueuePaginatedListDataSchema>;

const BrandModerationActionLogPaginatedListDataSchema = z
  .object({
    items: z.array(BrandModerationActionLogItemSchema),
    total: z.number(),
    page: z.number(),
    size: z.number(),
    pages: z.number(),
  })
  .passthrough();

export type BrandModerationActionLogPaginatedListPage = z.infer<typeof BrandModerationActionLogPaginatedListDataSchema>;

/** GET /api/brand/moderation/action-metrics — `data` payload */
const BrandModerationActionMetricsDataSchema = z
  .object({
    total_actions: z.number().nullable(),
    auto_responded_pct: z.number().nullable(),
    avg_confidence_pct: z.number().nullable(),
    avg_response_min: z.number().nullable(),
    review_rate_pct: z.number().nullable(),
  })
  .passthrough();

export type BrandModerationActionMetricsData = z.infer<typeof BrandModerationActionMetricsDataSchema>;

const BrandModerationActionMetricsResponseSchema = APIResponseSuccessSchema.extend({
  data: BrandModerationActionMetricsDataSchema.nullable(),
});

export type BrandModerationActionMetricsResponse = z.infer<typeof BrandModerationActionMetricsResponseSchema>;

/** GET /api/brand/moderation/response-agent — AI draft for a comment (query params) */
export type GenerateResponseParams = {
  platform: string;
  comment_id: string;
  comment_text: string;
  post_id: string;
  post_text: string;
  comment_user_name: string;
};

const GenerateResponsePlaybookUsedSchema = z
  .object({
    name: z.string().nullable(),
    version: z.number(),
    neo4j_id: z.string(),
  })
  .passthrough();

/** GET /api/brand/moderation/response-agent — `data` payload */
const GenerateResponseDataSchema = z
  .object({
    response_text: z.string(),
    category: z.string(),
    confidence_score: z.number(),
    reasoning: z.string(),
    matched_rules: z.array(z.unknown()),
    playbook_used: GenerateResponsePlaybookUsedSchema,
    products_used: z.array(z.unknown()),
    tone: z.string(),
  })
  .passthrough();

export type GenerateResponseData = z.infer<typeof GenerateResponseDataSchema>;

const GenerateResponseResponseSchema = APIResponseSuccessSchema.extend({
  data: GenerateResponseDataSchema.optional(),
}).passthrough();

export type GenerateResponseResponse = z.infer<typeof GenerateResponseResponseSchema>;

/** DELETE /api/brand/moderation/queue/{id} — remove item from moderation queue */
const RemoveQueueItemResponseSchema = APIResponseSuccessSchema.extend({
  data: z.unknown().optional(),
}).passthrough();

export type RemoveQueueItemResponse = z.infer<typeof RemoveQueueItemResponseSchema>;

function generateResponseQueryString(params: GenerateResponseParams): string {
  return new URLSearchParams({
    platform: params.platform,
    comment_id: params.comment_id,
    comment_text: params.comment_text,
    post_id: params.post_id,
    post_text: params.post_text,
    comment_user_name: params.comment_user_name,
  }).toString();
}

const BrandModerationFlagPaginatedListResponseSchema = APIResponseSuccessSchema.extend({
  data: BrandModerationFlagPaginatedListDataSchema,
});

const BrandModerationQueuePaginatedListResponseSchema = APIResponseSuccessSchema.extend({
  data: BrandModerationQueuePaginatedListDataSchema,
});

const BrandModerationActionLogPaginatedListResponseSchema = APIResponseSuccessSchema.extend({
  data: BrandModerationActionLogPaginatedListDataSchema,
});

export type BrandModerationFlagPaginatedListResponse = z.infer<typeof BrandModerationFlagPaginatedListResponseSchema>;
export type BrandModerationQueuePaginatedListResponse = z.infer<typeof BrandModerationQueuePaginatedListResponseSchema>;
export type BrandModerationActionLogPaginatedListResponse = z.infer<typeof BrandModerationActionLogPaginatedListResponseSchema>;

export const ConsumerResearchApiFactory = {
  Endpoint: mBaseURL + "/api/brand/consumer",
  ReviewQueueEndpoint: mBaseURL + "/api/brand/moderation/queue",
  FlagsListEndpoint: mBaseURL + "/api/brand/moderation/flags",
  ActionLogsEndpoint: mBaseURL + "/api/brand/moderation/action-logs",
  ActionMetricsEndpoint: mBaseURL + "/api/brand/moderation/action-metrics",
  ResponseAgentEndpoint: mBaseURL + "/api/brand/moderation/response-agent",

  Keys: {
    ReviewQueue: (params?: FetchBrandModerationPagedParams) =>
      [
        "consumer-research",
        "brand-moderation",
        "queue",
        createQueryString({
          page: params?.page ?? 1,
          size: params?.size ?? BRAND_MODERATION_PAGE_SIZE,
          queue_type: params?.queue_type,
        }),
      ] as const,
    FlagsList: (params?: FetchBrandModerationPagedParams) =>
      ["consumer-research", "brand-moderation", "flags", createQueryString({ page: params?.page ?? 1, size: params?.size ?? BRAND_MODERATION_PAGE_SIZE })] as const,
    ActionLogs: (params?: ActionLogsParams) =>
      ["consumer-research", "brand-moderation", "action-logs", createQueryString({ page: params?.page ?? 1, size: params?.size ?? BRAND_MODERATION_PAGE_SIZE })] as const,
    GenerateResponse: (params: GenerateResponseParams) => ["consumer-research", "brand-moderation", "response-agent", generateResponseQueryString(params)] as const,
    ActionMetrics: () => ["consumer-research", "brand-moderation", "action-metrics"] as const,
  },

  Schemas: {
    Response: {
      OverviewKeyStats: APIResponseSuccessSchema.extend({
        data: ConsumerResearchOverviewStatsSchema,
      }),
      ReviewQueue: BrandModerationQueuePaginatedListResponseSchema,
      FlagsList: BrandModerationFlagPaginatedListResponseSchema,
      ActionLogs: BrandModerationActionLogPaginatedListResponseSchema,
      ActionMetrics: BrandModerationActionMetricsResponseSchema,
      GenerateResponse: GenerateResponseResponseSchema,
      RemoveQueueItem: RemoveQueueItemResponseSchema,
    },
  },
  Apis: {
    FetchOverviewKeyStats: async () => {
      const response = await api(createURLPath(ConsumerResearchApiFactory.Endpoint, "overview/stats"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ConsumerResearchApiFactory.Schemas.Response.OverviewKeyStats.parse);
    },

    /** GET /api/brand/moderation/queue — paginated brand moderation review queue */
    FetchReviewQueue: async (params?: FetchBrandModerationPagedParams, options?: ApiOptions) => {
      const query = createQueryString({
        page: params?.page ?? 1,
        size: params?.size ?? BRAND_MODERATION_PAGE_SIZE,
        queue_type: params?.queue_type === "all" ? undefined : params?.queue_type,
      });

      const response = await api(`${ConsumerResearchApiFactory.ReviewQueueEndpoint}?${query}`, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      try {
        const data = await response.json();
        const i = ConsumerResearchApiFactory.Schemas.Response.ReviewQueue.parse(data);

        console.log(i);
        return i;
      } catch (err) {
        console.log(err);
        throw new Error("Failed to parse review queue response");
      }
    },

    /** GET /api/brand/moderation/action-logs — paginated moderation action history */
    FetchActionLogs: async (params?: ActionLogsParams, options?: ApiOptions) => {
      const query = createQueryString({
        page: params?.page ?? 1,
        size: params?.size ?? BRAND_MODERATION_PAGE_SIZE,
      });

      const response = await api(`${ConsumerResearchApiFactory.ActionLogsEndpoint}?${query}`, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ConsumerResearchApiFactory.Schemas.Response.ActionLogs.parse);
    },

    /** GET /api/brand/moderation/action-metrics — moderation action aggregates */
    FetchActionMetrics: async (options?: ApiOptions) => {
      const response = await api(ConsumerResearchApiFactory.ActionMetricsEndpoint, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ConsumerResearchApiFactory.Schemas.Response.ActionMetrics.parse);
    },

    /** GET /api/brand/moderation/response-agent — generate AI response for a comment */
    GenerateResponse: async (params: GenerateResponseParams, options?: ApiOptions) => {
      const query = generateResponseQueryString(params);
      const response = await api(`${ConsumerResearchApiFactory.ResponseAgentEndpoint}?${query}`, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ConsumerResearchApiFactory.Schemas.Response.GenerateResponse.parse);
    },

    /** DELETE /api/brand/moderation/queue/{id} — remove a queue item */
    RemoveQueueItem: async (queueItemId: string | number, options?: ApiOptions) => {
      const response = await api(`${ConsumerResearchApiFactory.ReviewQueueEndpoint}/${queueItemId}`, {
        method: "DELETE",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ConsumerResearchApiFactory.Schemas.Response.RemoveQueueItem.parse);
    },
  },

  Queries: {
    ReviewQueue: (params?: FetchBrandModerationPagedParams, options?: QueryOptions<BrandModerationQueuePaginatedListResponse>) =>
      queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.ReviewQueue(params),
        queryFn: () => ConsumerResearchApiFactory.Apis.FetchReviewQueue(params),
        ...options,
      }),

    ActionLogs: (params?: ActionLogsParams, options?: QueryOptions<BrandModerationActionLogPaginatedListResponse>) =>
      queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.ActionLogs(params),
        queryFn: () => ConsumerResearchApiFactory.Apis.FetchActionLogs(params),
        ...options,
      }),

    ActionMetrics: (options?: QueryOptions<BrandModerationActionMetricsResponse>) =>
      queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.ActionMetrics(),
        queryFn: () => ConsumerResearchApiFactory.Apis.FetchActionMetrics(),
        ...options,
      }),

    GenerateResponse: (params: GenerateResponseParams, options?: QueryOptions<GenerateResponseResponse>) =>
      queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.GenerateResponse(params),
        queryFn: () => ConsumerResearchApiFactory.Apis.GenerateResponse(params),
        ...options,
      }),
  },
};
